import { Box, Button, makeStyles, TextField } from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';
import clsx from 'clsx';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import React from 'react';
import useAuth from 'src/hooks/useAuth';
import useIsMountedRef from 'src/hooks/useIsMountedRef';
import * as Yup from 'yup';
import { useHistory } from 'react-router-dom';

const useStyles = makeStyles(() => ({
  root: {}
}));

const CompleteSignUpForm = ({
  className,
  nextStage,
  email,
  password,
  ...rest
}) => {
  const classes = useStyles();
  const { handleNewPassword, login } = useAuth();
  const history = useHistory();
  const isMountedRef = useIsMountedRef();

  return (
    <Formik
      validateOnBlur={false}
      validateOnChange={false}
      initialValues={{
        family_name: '',
        given_name: '',
        password: '',
        confirmPassword: '',
        submit: null
      }}
      validationSchema={Yup.object().shape({
        given_name: Yup.string().required('Given name is required'),
        family_name: Yup.string().required('Family name is required'),
        password: Yup.string()
          .required('Password is required')
          .min(6, 'Minimum length is 6'),
        confirmPassword: Yup.string().oneOf(
          [Yup.ref('password'), null],
          'Passwords do not match'
        )
      })}
      onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
        try {
          await handleNewPassword(
            email,
            password,
            values.password,
            values.family_name,
            values.given_name
          );

          await login(email, values.password);

          if (isMountedRef.current) {
            setStatus({ success: true });
            setSubmitting(false);

            history.push('/app');
          }
        } catch (err) {
          console.error(err);
          if (isMountedRef.current) {
            setStatus({ success: false });
            setErrors({ submit: err.message });
            setSubmitting(false);
          }
        }
      }}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
        touched,
        values
      }) => (
        <form
          noValidate
          onSubmit={handleSubmit}
          className={clsx(classes.root, className)}
          {...rest}
        >
          {errors.submit && (
            <Box mb={3}>
              <Alert severity="error">
                <AlertTitle>Sign up failed.</AlertTitle>
                {errors.submit}
              </Alert>
            </Box>
          )}
          <Box mb={2}>
            <Alert severity="info">
              <AlertTitle>Complete sign up.</AlertTitle>
              Please enter your details below to complete sign up to the
              application.
            </Alert>
          </Box>
          <TextField
            error={Boolean(touched.given_name && errors.given_name)}
            fullWidth
            autoFocus
            helperText={touched.given_name && errors.given_name}
            label="Given name"
            margin="normal"
            name="given_name"
            onBlur={handleBlur}
            onChange={handleChange}
            type="text"
            value={values.given_name}
            variant="outlined"
          />
          <TextField
            error={Boolean(touched.family_name && errors.family_name)}
            fullWidth
            helperText={touched.family_name && errors.family_name}
            label="Family name"
            margin="normal"
            name="family_name"
            onBlur={handleBlur}
            onChange={handleChange}
            type="text"
            value={values.family_name}
            variant="outlined"
          />
          <TextField
            error={Boolean(touched.password && errors.password)}
            fullWidth
            helperText={touched.password && errors.password}
            label="New password"
            margin="normal"
            name="password"
            onBlur={handleBlur}
            onChange={handleChange}
            type="password"
            value={values.password}
            variant="outlined"
          />
          <TextField
            error={Boolean(touched.confirmPassword && errors.confirmPassword)}
            fullWidth
            helperText={touched.confirmPassword && errors.confirmPassword}
            label="Confirm new password"
            margin="normal"
            name="confirmPassword"
            onBlur={handleBlur}
            onChange={handleChange}
            type="password"
            value={values.confirmPassword}
            variant="outlined"
          />
          <Box mt={2} mb={1}>
            <Button
              color="secondary"
              disabled={isSubmitting}
              fullWidth
              size="large"
              type="submit"
              variant="contained"
            >
              Sign up
            </Button>
          </Box>
        </form>
      )}
    </Formik>
  );
};

CompleteSignUpForm.propTypes = {
  className: PropTypes.string
};

export default CompleteSignUpForm;
